import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import "./index.css";

export const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8787";

axios.defaults.withCredentials = true;

// --- Theme Config ---
export const THEMES = {
  dark: { bg: "#0d1117", surface: "#161b22", panel: "#1c2128", border: "#30363d", text: "#e6edf3", muted: "#8b949e", faint: "#484f58" },
  light: { bg: "#ffffff", surface: "#f6f8fa", panel: "#eaeef2", border: "#d0d7de", text: "#1f2328", muted: "#656d76", faint: "#9198a1" },
  monokai: { bg: "#272822", surface: "#3e3d32", panel: "#2d2c27", border: "#4e4d43", text: "#f8f8f2", muted: "#908b84", faint: "#605e54" },
  solarized: { bg: "#002b36", surface: "#073642", panel: "#04303d", border: "#0a4552", text: "#839496", muted: "#657b83", faint: "#4a6068" },
  nord: { bg: "#2e3440", surface: "#3b4252", panel: "#434c5e", border: "#4c566a", text: "#eceff4", muted: "#d8dee9", faint: "#9199a8" },
  rose: { bg: "#fff1f2", surface: "#ffe4e6", panel: "#fecdd3", border: "#fda4af", text: "#1c0506", muted: "#be123c", faint: "#e11d48" },
};
export const ACCENTS = ["#f78166", "#58a6ff", "#3fb950", "#d29922", "#bc8cff", "#e879f9", "#22d3ee", "#fb923c"];
export const FONTS = ["Inter", "JetBrains Mono", "DM Sans", "Fira Code", "Roboto Mono"];
export const DENSITIES = {
  compact: { py: 4, fs: 11 },
  comfortable: { py: 8, fs: 13 },
  spacious: { py: 12, fs: 14 },
};

export const ThemeCtx = createContext();
export const AuthCtx = createContext();

export function useTheme() { return useContext(ThemeCtx); }
export function useAuth() { return useContext(AuthCtx); }

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) return (
      <div style={{ padding: 40, fontFamily: "sans-serif", color: "#f85149", textAlign: "center" }}>
        <h2>Oops, something broke in the application.</h2>
        <p style={{ color: "#8b949e", fontSize: 13, marginBottom: 20 }}>{this.state.error?.message}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()} style={{ padding: "8px 16px" }}>
          Reload Application
        </button>
      </div>
    );
    return this.props.children;
  }
}

function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [init, setInit] = useState(false);

  const [theme, setTheme] = useState("dark");
  const [accent, setAccent] = useState("#f78166");
  const [font, setFont] = useState("Inter");
  const [density, setDensity] = useState("comfortable");

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.setProperty('--color-accent', accent);
    document.documentElement.style.setProperty('--font-sans', font);
    document.documentElement.style.setProperty('--padding-y', `${DENSITIES[density]?.py || 8}px`);
    document.documentElement.style.setProperty('--font-size-base', `${DENSITIES[density]?.fs || 13}px`);
  }, [theme, accent, font, density]);

  const applyTheme = (userData) => {
    if (userData.theme && Object.keys(userData.theme).length > 0) {
      setTheme(userData.theme.mode || "dark");
      setAccent(userData.theme.accent || "#f78166");
      setFont(userData.theme.font || "Inter");
      setDensity(userData.theme.density || "comfortable");
    }
  };

  const login = (userData) => {
    setUser(userData);
    applyTheme(userData);
  };

  const logout = async () => {
    try { await axios.post(`${API_URL}/auth/logout`); } catch { }
    setUser(null);
  };

  useEffect(() => {
    axios.get(`${API_URL}/auth/me`)
      .then(res => {
        setUser(res.data);
        applyTheme(res.data);
      })
      .catch(() => setUser(null))
      .finally(() => setInit(true));
  }, []);

  return (
    <AuthCtx.Provider value={{ user, init, login, logout }}>
      <ThemeCtx.Provider value={{ C: THEMES[theme], theme, setTheme, accent, setAccent, font, setFont, density, setDensity }}>
        {children}
      </ThemeCtx.Provider>
    </AuthCtx.Provider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<AuthGuard reverse><AuthPage /></AuthGuard>} />
            <Route path="/*" element={<AuthGuard guestAllowed><Dashboard /></AuthGuard>} />
          </Routes>
        </BrowserRouter>
      </AppProvider>
    </ErrorBoundary>
  );
}

function AuthGuard({ children, reverse = false, guestAllowed = false }) {
  const { user, init } = useAuth();

  if (!init) return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", alignItems: "center", justifyContent: "center", background: "var(--color-bg)" }}>
      <div className="spinner" style={{ marginBottom: 20 }}></div>
      <div style={{ color: "var(--color-muted)", fontSize: 13, fontFamily: "var(--font-sans)" }}>Loading AutoFlow...</div>
    </div>
  );

  if (reverse) {
    if (user) return <Navigate to="/" />;
    return children;
  }

  if (!user && !guestAllowed) return <Navigate to="/login" />;
  return children;
}